import { Button, Card, CardTitle, EmptyState, RichTextEditor } from '@wundo/ui';
import { ArrowRight, Sparkles } from 'lucide-react';
import type {
  MeetingPageResponse,
  MeetingPrepContent,
} from '@wundo/shared/meetingPage.ts';
import { MeetingActionItems } from './MeetingActionItems.tsx';
import { MeetingCopyButton } from './MeetingCopyButton.tsx';
import { MeetingRegenerateRow } from './MeetingRegenerateRow.tsx';
import type { MeetingActionItemHandlers } from './meetingActionItemHandlers.ts';
import { meetingIsOver } from './meetingIsOver.ts';
import { useAutosavedDocument } from './useAutosavedDocument.ts';
import type { DocumentSaveOptions } from '@/lib/meetingPage.ts';
import { setChatDrawerState } from '@/lib/chatDrawerState.ts';
import { askChatForOpening } from '@/lib/chatOpeningAsk.ts';
import type { RichTextEditorWiring } from '@/lib/richTextEditor.ts';
import { m } from '@/paraglide/messages.js';

export interface MeetingPrepPanelProps {
  page: MeetingPageResponse;
  /** Whether a generation is in flight, so the panel can say so. */
  generating: boolean;
  generateFailed: boolean;
  /** Whether the copy just landed, so the button can say so for a moment. */
  copied: boolean;
  editor: RichTextEditorWiring;
  actionItems: MeetingActionItemHandlers;
  onGenerate: () => void;
  onRegenerate: () => void;
  onSave: (content: MeetingPrepContent, options: DocumentSaveOptions) => void;
  onCopy: () => void;
}

/**
 * The Agenda tab: what to raise in the conversation to come, with the thread's
 * open agreements under it.
 */
export function MeetingPrepPanel({
  page,
  generating,
  generateFailed,
  copied,
  editor,
  actionItems,
  onGenerate,
  onRegenerate,
  onSave,
  onCopy,
}: MeetingPrepPanelProps) {
  const fromServer = page.prep?.content ?? null;
  const [document, write] = useAutosavedDocument(fromServer, onSave);

  /*
   * A meeting Wundo stays out of keeps the tab, so the page does not change shape under a
   * switch.
   */
  if (!page.prep_available && !page.prep_intake && !document) {
    return <p className='mt-6 text-base text-ink-muted'>{unavailableReason(page)}</p>;
  }

  return (
    <div className='mt-6 flex flex-col gap-6'>
      {/* One card: the themes and the agreements under them are one document. */}
      <Card as='section' padding='document'>
        <div className='flex items-center justify-between gap-3'>
          <CardTitle>{m.meeting_tab_prep()}</CardTitle>
          {document && <MeetingCopyButton copied={copied} onCopy={onCopy} />}
        </div>

        {!document && page.prep_intake && <PrepIntakeInvite />}

        {!document && !page.prep_intake && (
          <PrepGenerateInvite
            generating={generating}
            failed={generateFailed}
            onGenerate={onGenerate}
          />
        )}

        {document && (
          <div className='mt-4'>
            {/*
              The headings came from the template at generation time and are the user's
              now.
            */}
            <RichTextEditor
              variant='bare'
              contentClassName='text-ink-body'
              ariaLabel={m.meeting_prep_editor_label()}
              placeholder={m.meeting_prep_section_empty()}
              value={document.text}
              onChange={(text) => write({ text })}
              {...editor}
            />

            {/* A rule above the agreements: they belong to the conversation, not to a theme. */}
            <div className='mt-5 border-t border-fill pt-5'>
              <MeetingActionItems
                items={page.action_items}
                title={m.meeting_prep_actions_title()}
                emptyLabel={m.meeting_prep_actions_empty()}
                threadId={page.thread?.id ?? null}
                doneCount={page.action_items_done_count}
                {...actionItems}
              />
            </div>
          </div>
        )}

        {document && (
          <MeetingRegenerateRow
            available={page.prep_available}
            generating={generating}
            failed={generateFailed}
            labels={{
              regenerate: m.meeting_prep_regenerate(),
              generating: m.meeting_prep_generating(),
              failed: m.meeting_prep_generate_failed(),
            }}
            onRegenerate={onRegenerate}
          />
        )}
      </Card>
    </div>
  );
}

/*
 * Nothing to write a preparation from yet: the questions that would fill it are
 * asked in the chat beside the page, so this state points there and offers no
 * generate button.
 */
function PrepIntakeInvite() {
  return (
    <EmptyState
      headingLevel='h3'
      title={m.meeting_prep_intake_title()}
      description={m.meeting_prep_intake_body()}
      action={
        <Button
          size='sm'
          variant='ghost'
          /* The drawer may be open already: the question itself has to come forward. */
          onClick={() => {
            setChatDrawerState('open');
            askChatForOpening();
          }}
        >
          {m.meeting_prep_intake_cta()}
          <ArrowRight className='size-4' aria-hidden='true' />
        </Button>
      }
    />
  );
}

interface PrepGenerateInviteProps {
  generating: boolean;
  failed: boolean;
  onGenerate: () => void;
}

/* Written in place: the meeting stays readable while this button says one is coming. */
function PrepGenerateInvite({ generating, failed, onGenerate }: PrepGenerateInviteProps) {
  return (
    <EmptyState
      headingLevel='h3'
      title={m.meeting_prep_invite_title()}
      busy={generating}
      description={
        generating ? m.meeting_prep_generating() : m.meeting_prep_invite_body()
      }
      action={
        <div className='flex flex-col items-center gap-2'>
          {failed && (
            <p role='alert' className='text-sm text-danger'>
              {m.meeting_prep_generate_failed()}
            </p>
          )}
          <Button size='sm' loading={generating} onClick={onGenerate}>
            {!generating && <Sparkles className='size-4' aria-hidden='true' />}
            {failed ? m.meeting_prep_generate_retry() : m.meeting_prep_generate()}
          </Button>
        </div>
      }
    />
  );
}

/*
 * Why nothing can be generated here, in one line. Over comes first: a meeting
 * that already happened is not prepared, whatever Wundo's part in it was.
 */
function unavailableReason(page: MeetingPageResponse): string {
  if (meetingIsOver(page, new Date())) return m.meeting_prep_meeting_over();
  return page.decision.follow
    ? m.meeting_prep_needs_thread()
    : m.meeting_prep_not_followed();
}
